/**
 * Сетевой график (ПЛ-26): работы — блоками по столбцам логики, связи — линиями. Ведущие связи сплошные,
 * остальные пунктиром; критические — красным. Нажатие выбирает работу, двойное нажатие открывает карточку.
 */
import { useMemo } from 'react';
import { Button } from '../../ui/button';
import { EmptyState } from '../../ui/feedback';
import { fmtDur, fmtMoment, hpd, type View } from './model';
import { networkLayout } from './trace';

const W = 184;
const H = 82;
const GX = 56;
const GY = 18;
const PAD = 16;
const LIMIT = 1500;

export function NetworkView({
  view,
  only,
  selectedId,
  onSelect,
  onOpen,
  height,
}: {
  view: View;
  /** Какие работы показывать (отбор и трассировка); null — все */
  only: Set<string> | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
  height: string;
}) {
  const tooMany = (only ? only.size : view.activities.length) > LIMIT;
  const lay = useMemo(() => (tooMany ? null : networkLayout(view, only)), [view, only, tooMany]);
  const byId = useMemo(() => new Map(view.activities.map((a) => [a.id, a])), [view.activities]);
  if (tooMany)
    return (
      <EmptyState title="Слишком много работ для сетевого графика">
        Выберите работу и включите трассировку логики или отберите критические — покажем до {LIMIT} работ.
      </EmptyState>
    );
  if (!lay?.nodes.length) return <EmptyState title="Нет работ под отбор" />;
  const pos = new Map(lay.nodes.map((n) => [n.id, n]));
  let rows = 0;
  for (const n of lay.nodes) rows = Math.max(rows, n.row + 1);
  const width = PAD * 2 + lay.cols * W + (lay.cols - 1) * GX;
  const hgt = PAD * 2 + rows * H + (rows - 1) * GY;
  const x = (c: number) => PAD + c * (W + GX);
  const y = (r: number) => PAD + r * (H + GY);
  const h = hpd(view);
  const edges = view.relations.filter((r) => pos.has(r.predId) && pos.has(r.succId));

  const sel = selectedId && pos.has(selectedId) ? byId.get(selectedId) : undefined;
  return (
    <>
      {sel ? (
        <Button onClick={() => onOpen(sel.id)} className="self-start max-md:h-11">
          Открыть карточку {sel.code}
        </Button>
      ) : null}
      <div
        className="overflow-auto rounded-md border border-line bg-surface"
        style={{ height, maxHeight: Math.max(240, hgt + 4) }}
      >
        <svg
          width={width}
          height={hgt}
          role="img"
          aria-label={`Сетевой график: работ ${lay.nodes.length}, связей ${edges.length}`}
        >
          <defs>
            <marker
              id="net-arrow"
              viewBox="0 0 8 8"
              refX="8"
              refY="4"
              markerWidth="7"
              markerHeight="7"
              orient="auto"
            >
              <path d="M0 0 L8 4 L0 8 Z" fill="context-stroke" />
            </marker>
          </defs>
          {edges.map((r) => {
            const a = pos.get(r.predId)!;
            const b = pos.get(r.succId)!;
            const fromStart = r.type === 'SS' || r.type === 'SF';
            const toFinish = r.type === 'FF' || r.type === 'SF';
            const x1 = fromStart ? x(a.col) : x(a.col) + W;
            const x2 = toFinish ? x(b.col) + W : x(b.col);
            const y1 = y(a.row) + H / 2;
            const y2 = y(b.row) + H / 2;
            const bend = Math.max(24, Math.abs(x2 - x1) / 2);
            const c1 = fromStart ? x1 - bend : x1 + bend;
            const c2 = toFinish ? x2 + bend : x2 - bend;
            const crit =
              r.driving && byId.get(r.predId)?.dates?.critical && byId.get(r.succId)?.dates?.critical;
            return (
              <path
                key={r.id}
                d={`M${x1} ${y1} C${c1} ${y1} ${c2} ${y2} ${x2} ${y2}`}
                fill="none"
                stroke={crit ? 'var(--color-danger)' : 'var(--color-ink-2)'}
                strokeWidth={r.driving ? 1.5 : 1}
                strokeDasharray={r.driving ? undefined : '4 3'}
                markerEnd="url(#net-arrow)"
                opacity={r.driving ? 0.9 : 0.55}
              >
                <title>
                  {byId.get(r.predId)?.code} → {byId.get(r.succId)?.code}: {r.type}
                  {r.lagMin ? `, задержка ${fmtDur(r.lagMin, h)}` : ''}
                  {r.driving ? ', ведущая' : ''}
                </title>
              </path>
            );
          })}
          {lay.nodes.map((n) => {
            const a = byId.get(n.id)!;
            const d = a.dates;
            const ms = a.type === 'start_ms' || a.type === 'finish_ms';
            const sel = n.id === selectedId;
            const tone = d?.critical
              ? 'var(--color-danger)'
              : d?.nearCritical
                ? 'var(--color-warning)'
                : 'var(--color-line-strong)';
            const tf = d?.tf;
            return (
              // biome-ignore lint/a11y/useSemanticElements: блок SVG, кнопкой быть не может
              <g
                key={n.id}
                transform={`translate(${x(n.col)} ${y(n.row)})`}
                role="button"
                tabIndex={0}
                aria-label={`${a.code} ${a.name}`}
                aria-pressed={sel}
                className="cursor-pointer outline-none"
                onClick={() => onSelect(n.id)}
                onDoubleClick={() => onOpen(n.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onOpen(n.id);
                  if (e.key === ' ') {
                    e.preventDefault();
                    onSelect(n.id);
                  }
                }}
              >
                <rect
                  width={W}
                  height={H}
                  rx={ms ? 18 : 6}
                  fill={a.actualFinish ? 'var(--color-surface-2)' : 'var(--color-surface)'}
                  stroke={sel ? 'var(--color-accent)' : tone}
                  strokeWidth={sel ? 3 : d?.critical ? 2 : 1}
                />
                <text x={10} y={18} className="fill-ink text-[12px] font-semibold">
                  {a.code}
                </text>
                <text x={W - 10} y={18} textAnchor="end" className="fill-ink-2 text-[11px]">
                  {tf == null ? '' : `резерв ${fmtDur(tf, h)}`}
                </text>
                <text x={10} y={37} className="fill-ink text-[12px]">
                  {a.name.length > 26 ? `${a.name.slice(0, 25)}…` : a.name}
                </text>
                <text x={10} y={56} className="fill-ink-2 text-[11px]">
                  {fmtMoment(a.actualStart ?? d?.es)} — {fmtMoment(a.actualFinish ?? d?.ef)}
                </text>
                <text x={10} y={73} className="fill-ink-2 text-[11px]">
                  {ms ? 'веха' : fmtDur(a.durationMin, h)}
                  {a.percent ? ` · ${a.percent}%` : ''}
                </text>
                <title>{a.name}</title>
              </g>
            );
          })}
        </svg>
      </div>
    </>
  );
}
